import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { StatusBadge } from "../components/Badges";
import {
  Search, CheckCircle2, UserCog, CalendarDays, PackageCheck, Clock, ArrowLeft,
} from "lucide-react";

const BG = "https://static.prod-images.emergentagent.com/jobs/9aa213a6-9b93-496d-b454-969c7d67f960/images/548aa7063aaf94d6e582a4e7ce5170638e4218c32988b7d7547757e7214e22f7.png";
const LOGO = "https://static.prod-images.emergentagent.com/jobs/9aa213a6-9b93-496d-b454-969c7d67f960/images/80b5831e8eb35a7d67db619b6b98959a8312a899cbdbc39b43bf61ef5e0fcc19.png";

const STAGES = ["Registered", "Assigned", "In Progress", "Resolved", "Closed"];

export default function PublicTrack() {
  const [params] = useSearchParams();
  const [complaintNo, setComplaintNo] = useState(params.get("no") || "");
  const [mobile, setMobile] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e?.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const r = await api.post("/public/track", { complaint_no: complaintNo, mobile });
      setResult(r.data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("no")) setComplaintNo(params.get("no")); }, [params]);

  const currentStage = result ? STAGES.indexOf(result.status) : -1;

  return (
    <div className="min-h-screen relative">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-white/85 backdrop-blur-sm" />

      <div className="relative max-w-2xl mx-auto px-5 py-10 sm:py-16">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO} alt="RoSo" className="h-11 w-11 object-contain" />
          <div>
            <div className="font-heading font-black text-xl leading-none">RoSo Multi Trading</div>
            <div className="label-caps text-muted-foreground mt-1">Track your service request</div>
          </div>
        </div>

        <div className="border border-border bg-white p-6 sm:p-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-2">Track complaint</h1>
          <p className="text-muted-foreground mb-6 text-sm">Enter your complaint number and registered mobile number.</p>

          <form onSubmit={track} className="space-y-4">
            <div>
              <Label className="label-caps">Complaint number</Label>
              <Input value={complaintNo} data-testid="track-complaint-no" placeholder="RMT20260529AC001"
                onChange={(e) => setComplaintNo(e.target.value)} className="mt-1 rounded-sm font-mono" required />
            </div>
            <div>
              <Label className="label-caps">Mobile number</Label>
              <Input value={mobile} data-testid="track-mobile" placeholder="Registered mobile"
                onChange={(e) => setMobile(e.target.value)} className="mt-1 rounded-sm" required />
            </div>
            {error && <div data-testid="track-error" className="bg-signal/10 border border-signal text-signal text-sm px-3 py-2">{error}</div>}
            <Button type="submit" disabled={loading} data-testid="track-submit-button" className="w-full h-11 rounded-sm bg-klein hover:bg-klein/90 text-white font-semibold">
              <Search size={16} className="mr-1" /> {loading ? "Searching…" : "Track"}
            </Button>
          </form>
        </div>

        {result && (
          <div className="border border-border bg-white mt-6 p-6 sm:p-8 fade-up" data-testid="track-result">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="font-mono text-klein font-bold">{result.complaint_no}</span>
              <StatusBadge status={result.status} />
            </div>
            <div className="mt-3 text-sm text-muted-foreground">{result.appliance_type} · {result.issue_category}</div>

            {/* Progress timeline */}
            <div className="mt-6">
              <div className="label-caps text-muted-foreground mb-4">Status</div>
              <div className="flex items-center">
                {STAGES.map((stage, i) => {
                  const done = i <= currentStage;
                  return (
                    <div key={stage} className="flex-1 flex flex-col items-center relative">
                      {i > 0 && <div className={`absolute right-1/2 top-3 h-0.5 w-full ${i <= currentStage ? "bg-klein" : "bg-border"}`} />}
                      <div className={`relative z-10 h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? "bg-klein text-white" : "bg-secondary text-muted-foreground border border-border"}`}>
                        {done ? "✓" : i + 1}
                      </div>
                      <div className={`text-[10px] mt-2 text-center ${done ? "text-ink font-semibold" : "text-muted-foreground"}`}>{stage}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2"><UserCog size={15} className="text-klein" /> Engineer: <span className="font-medium">{result.engineer_name || "Not assigned yet"}</span></div>
              <div className="flex items-center gap-2"><CalendarDays size={15} className="text-klein" /> Visit: <span className="font-medium">{result.scheduled_visit_date ? new Date(result.scheduled_visit_date).toLocaleDateString() : "To be scheduled"}</span></div>
            </div>

            {/* Activity timeline */}
            {result.timeline?.length > 0 && (
              <div className="mt-6 border-t border-border pt-4">
                <div className="label-caps text-muted-foreground mb-3">History</div>
                <div className="space-y-3">
                  {result.timeline.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm">
                      <Clock size={13} className="text-muted-foreground" />
                      <span className="font-medium">{t.action}</span>
                      <span className="text-muted-foreground text-xs ml-auto">{new Date(t.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.status === "Resolved" && (
              <div className="mt-5 flex items-center gap-2 text-emerald-700 text-sm"><CheckCircle2 size={16} /> Your complaint has been resolved. Thank you!</div>
            )}
          </div>
        )}

        <Link to="/login" className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-klein" data-testid="staff-login-link">
          <ArrowLeft size={14} /> Staff sign in
        </Link>
      </div>
    </div>
  );
}
